export type BidirectionalMap<TGql extends string, TInternal extends string> = {
  fromGql: Record<TGql, TInternal>;
  toGql: Record<TInternal, TGql>;
};

/**
 * Creates a bidirectional map between GraphQL enum values and internal values.
 * Pass the GraphQL enum type as a generic to enforce exhaustiveness at the call site.
 *
 * Usage: `createBidirectionalMap<ProjectType>()({ STANDARD: 'standard', ... })`
 */
export function createBidirectionalMap<TGql extends string = string>(
  ..._: string extends TGql
    ? [guard: 'ERROR: specify a GraphQL enum type parameter']
    : []
) {
  return function <const TMap extends Record<TGql, string>>(
    gqlToInternal: TMap
  ): BidirectionalMap<Extract<keyof TMap, string>, TMap[keyof TMap]> {
    const internalToGql = Object.fromEntries(
      Object.entries(gqlToInternal).map(([k, v]) => [v, k])
    ) as Record<TMap[keyof TMap], Extract<keyof TMap, string>>;

    return {
      fromGql: gqlToInternal as unknown as Record<
        Extract<keyof TMap, string>,
        TMap[keyof TMap]
      >,
      toGql: internalToGql,
    };
  };
}
